import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import {
  collection,
  Firestore,
  addDoc,
  Timestamp,
  doc,
  updateDoc,
} from '@angular/fire/firestore';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-dlg-create-event',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './dlg-create-event.component.html',
  styleUrl: './dlg-create-event.component.css',
})
export class DlgCreateCalendarEventComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<DlgCreateCalendarEventComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private readonly afs: Firestore = inject(Firestore);
  private readonly toastr: ToastrService = inject(ToastrService);

  groupId: string | null = null;
  working = false;
  pageForm: FormGroup = new FormGroup({});

  ngOnDestroy(): void {
    document.body.style.overflow = 'auto';
  }

  ngOnInit(): void {
    document.body.style.overflow = 'hidden';
    this.groupId = this.data.groupId;

    const isEdit = !!this.data.event;
    const event = this.data.event;

    let formattedDate = '';
    if (isEdit && event) {
      const dateSource = event.parsedEventDate || event.eventDate || event.date;
      let dateObj: Date | null = null;
      if (dateSource) {
        if (dateSource instanceof Date) {
          dateObj = dateSource;
        } else if (typeof dateSource.toDate === 'function') {
          dateObj = dateSource.toDate();
        } else if (dateSource.seconds !== undefined) {
          dateObj = new Date(dateSource.seconds * 1000);
        } else {
          dateObj = new Date(dateSource);
        }
      }
      if (dateObj) {
        formattedDate = moment(dateObj).format('YYYY-MM-DDTHH:mm');
      }
    }

    const hasVolunteers = isEdit && event && event.volunteersRequired > 0;

    this.pageForm = new FormGroup({
      name: new FormControl(isEdit ? event.name : '', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
      ]),
      date: new FormControl(isEdit ? formattedDate : '', [Validators.required]),
      description: new FormControl(isEdit ? event.description : '', [
        Validators.maxLength(5000),
      ]),
      requestVolunteers: new FormControl(hasVolunteers),
      volunteersRequired: new FormControl(isEdit ? event.volunteersRequired || 0 : 0),
      volunteerCriteria: new FormControl(isEdit ? event.volunteerCriteria || '' : ''),
    });

    const requestVolunteersControl = this.pageForm.get('requestVolunteers');
    const volunteersRequiredControl = this.pageForm.get('volunteersRequired');

    // Set initial validators
    if (requestVolunteersControl?.value) {
      volunteersRequiredControl?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      volunteersRequiredControl?.clearValidators();
    }
    volunteersRequiredControl?.updateValueAndValidity();

    // Listen to changes
    requestVolunteersControl?.valueChanges.subscribe((req) => {
      if (req) {
        volunteersRequiredControl?.setValidators([Validators.required, Validators.min(1)]);
        if (!volunteersRequiredControl?.value || volunteersRequiredControl.value < 1) {
          volunteersRequiredControl?.setValue(1);
        }
      } else {
        volunteersRequiredControl?.clearValidators();
      }
      volunteersRequiredControl?.updateValueAndValidity();
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  async submit() {
    if (this.pageForm.invalid || !this.groupId) return;
    this.working = true;
    const isEdit = !!this.data.event;

    try {
      const eventMoment = moment(this.pageForm.value.date);

      const eventData: any = {
        name: this.pageForm.value.name,
        description: this.pageForm.value.description || '',
        date: Timestamp.fromDate(eventMoment.toDate()),
        eventDate: Timestamp.fromDate(eventMoment.toDate()),
        month: eventMoment.month() + 1,
        year: eventMoment.year(),
        day: eventMoment.date(),
        volunteersRequired: this.pageForm.value.requestVolunteers
          ? this.pageForm.value.volunteersRequired || 0
          : 0,
        volunteerCriteria: this.pageForm.value.requestVolunteers
          ? this.pageForm.value.volunteerCriteria || ''
          : '',
      };

      if (isEdit) {
        const eventDocRef = doc(
          this.afs,
          'groups',
          this.groupId,
          'events_calendar',
          this.data.event.id
        );
        await updateDoc(eventDocRef, eventData);
        this.toastr.success('Event updated successfully.');
      } else {
        eventData.groupId = this.groupId;
        eventData.type = 'notice';
        eventData.volunteers = [];

        const calendarRef = collection(
          this.afs,
          'groups',
          this.groupId,
          'events_calendar'
        );
        await addDoc(calendarRef, eventData);
        this.toastr.success('Event created successfully.');
      }
      this.dialogRef.close(true);
    } catch (err) {
      this.toastr.error(
        isEdit ? 'Failed to update event.' : 'Failed to create event.'
      );
    } finally {
      this.working = false;
    }
  }
}
